// import global variables.
import {BODY_ELEMENT, FOCUS_ELEMENTS, randomString, tabindexControl} from '../utils/global';
import * as backdrop from '../utils/backdrop';
import scrollLock from '../utils/scrollLock';
import {Carousel} from './carousel';

class Lightbox {
    /**
     * ライトボックス
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     * @param {Object} carouselOptions - Carouselクラスの設定の変更をする際のオブジェクト
     */
    constructor(root, options, carouselOptions) {
        const config = {
            zoomButtonCloneTargetClassName: 'p-carousel-product-indicator__item',
            className: {
                ctrl: 'js-lightbox__ctrl',
                wrap: 'p-lightbox',
                inner: 'p-lightbox__inner',
                list: 'p-lightbox__list',
                item: 'p-lightbox__item',
                closeButton: 'p-lightbox__close',
                zoomButton: 'p-lightbox__zoom',
                zoomButtonWrap: 'p-button-zoom',
                altText: 'u-altText'
            },
            prefix: {
                id: 'lightbox-'
            }
        };

        // ルートとなる要素が無い場合は何もしない
        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.id = randomString(this.config.prefix.id);
        this.root = root;
        this.ctrls = this.root.querySelectorAll(`.${this.config.className.ctrl}`);
        this.closeButton = document.createElement('button');
        this.lightbox = this.createLightbox();
        this.zoomButton = this.createZoomButton();
        this.backdrop = backdrop.createBackdropLayer();
        this.lightboxInCarousel = this.ctrls.length > 1 ? new Carousel(this.lightbox, carouselOptions) : null;
        this.focusableElements = Array.from(this.lightbox.querySelectorAll(FOCUS_ELEMENTS));
        this.currentIndex = 0;
        this.isOpen = false;

        this.init();
        this.addEvent();
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        // ライトボックスが生成されていない場合は何もしない
        if (!this.lightbox) {
            return;
        }

        BODY_ELEMENT.insertAdjacentElement('beforeend', this.lightbox);
        this.focusableElements.push(this.closeButton);

        // コントロールボタンの実装1
        for (const ctrl of this.ctrls) {
            // button要素以外にはbutton roleを追加
            if (this.root.tagName.toLowerCase() !== 'button') {
                ctrl.setAttribute('role', 'button');
                ctrl.tabIndex = 0;
            }

            ctrl.setAttribute('aria-expanded', 'false');
            ctrl.setAttribute('aria-haspopup', 'true');
            ctrl.setAttribute('aria-controls', this.id);
        }
    }

    /**
     * createLightbox - ライトボックスを生成
     * @return {HTMLElement}
     */
    createLightbox() {
        const wrap = document.createElement('div');
        const items = [];

        wrap.id = this.id;
        wrap.hidden = true;
        wrap.tabIndex = -1;
        wrap.dataset.indicator = 'false';
        wrap.classList.add(this.config.className.wrap);
        wrap.setAttribute('role', 'dialog');
        wrap.setAttribute('aria-modal', 'true');

        this.closeButton.type = 'button';
        this.closeButton.classList.add(this.config.className.closeButton);
        this.closeButton.insertAdjacentHTML('beforeend', `<span class="${this.config.className.altText}">閉じる</span>`);

        for (const ctrl of this.ctrls) {
            items.push(`<li class="${this.config.className.item}"><img src="${ctrl.getAttribute('href')}" alt="" decoding="async"></li>`);
        }

        wrap.insertAdjacentHTML('beforeend', `<div class="${this.config.className.inner}"><ul class="${this.config.className.list}">${items.join('')}</ul></div>`);
        wrap.insertAdjacentElement('beforeend', this.closeButton);

        return wrap;
    }

    /**
     * createZoomButton - 拡大ボタンを生成
     * @return {HTMLElement}
     */
    createZoomButton() {
        const rootParent = this.root.parentNode;
        const cloneTarget = rootParent.querySelector(`.${this.config.zoomButtonCloneTargetClassName}`);
        const buttonWrap = !cloneTarget ? document.createElement('p') : cloneTarget.cloneNode();

        buttonWrap.classList.add(this.config.className.zoomButtonWrap);
        buttonWrap.insertAdjacentHTML('beforeend', `<button type="button" class="${this.config.className.zoomButton}"><span class="${this.config.className.altText}">大きい画像を見る</span></button>`);

        // カルーセルのインジケーターがない場合ルート要素の親要素内にボタンを追加
        if (!cloneTarget) {
            rootParent.insertAdjacentElement('beforeend', buttonWrap);

        // カルーセルのインジケーターがある場合インジケータリスト内にボタンを追加
        } else {
            cloneTarget.parentNode.insertAdjacentElement('beforeend', buttonWrap);
        }

        return buttonWrap.lastElementChild;
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        for (const ctrl of this.ctrls) {
            ctrl.addEventListener('click', (e) => {
                const index = [].indexOf.call(this.ctrls, e.currentTarget);

                e.preventDefault();
                this.lightboxOpen(index);
            });
        }

        this.zoomButton.addEventListener('click', () => {
            const index = [].indexOf.call(this.ctrls, Array.from(this.ctrls).filter((element) => !element.parentNode.hidden)[0]);

            this.lightbox.dataset.openFromZoom = true;
            this.lightboxOpen(index);
        });

        this.closeButton.addEventListener('click', this.lightboxClose.bind(this));
        this.lightbox.addEventListener(backdrop.eventName, this.lightboxClose.bind(this));

        this.lightbox.addEventListener('keydown', (e) => {
            // 閉じている場合は無効
            if (!this.isOpen || e.key.indexOf('Esc') === -1) {
                return;
            }

            // Escapeキー押下で閉じる
            this.lightboxClose(event);
        });
    }

    /**
     * lightboxOpen - 開く処理
     * @param  {Number} index 開いたときに表示しておくモーダル内カルーセルのindex番号
     * @return {Void}
     */
    lightboxOpen(index) {
        // すでに展開されている場合は何もしない
        if (this.isOpen) {
            return;
        }

        // フラグを更新
        this.isOpen = true;
        this.lightbox.hidden = false;

        // カレントとindex番号が一致しない場合は更新
        if (index !== this.currentIndex) {
            this.currentIndex = index;
        }

        if (this.ctrls.length > 1) {
            this.lightboxInCarousel.slide(this.currentIndex);
        }

        this.ctrls[this.currentIndex].setAttribute('aria-expanded', 'true');
        this.lightbox.focus();

        // タブインデックスの更新・スクロールの制御
        tabindexControl(true, this.focusableElements);
        backdrop.displayBackdropLayer(true, this.id);
        scrollLock(true);
    }

    /**
     * lightboxClose - 閉じる処理
     * @return {Void}
     */
    lightboxClose() {
        // すでに閉じられている場合は何もしない
        if (!this.isOpen) {
            return;
        }

        // フラグを更新
        this.isOpen = false;
        this.lightbox.hidden = true;
        this.ctrls[this.currentIndex].setAttribute('aria-expanded', 'false');

        // ズームボタンをトリガーにライトボックスを開いた場合
        if (this.lightbox.dataset.openFromZoom === 'true') {
            this.lightbox.dataset.openFromZoom = false;
            this.zoomButton.focus();

        // ズームボタン以外から開いた場合
        } else {
            this.ctrls[this.currentIndex].focus();
        }

        // タブインデックスの更新・スクロールの制御
        tabindexControl(false, this.focusableElements);
        backdrop.displayBackdropLayer(false);
        scrollLock(false);
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    // ライトボックス
    document.querySelectorAll('.js-lightbox').forEach((element) => new Lightbox(element, {}, {
        roop: true,
        className: {
            carousel: {
                list: 'p-lightbox__list',
                item: 'p-lightbox__item',
                animate: 'is-animate',
                swipe: 'is-swipe',
                reverse: 'is-reverse',
                current: 'is-current',
                next: 'is-next',
                previous: 'is-previous'
            },
            controller: {
                wrap: 'p-lightbox-controller',
                list: 'p-lightbox-controller__list',
                item: 'p-lightbox-controller__item',
                button: 'p-lightbox-controller__button',
                previous: 'p-lightbox-controller__button--previous',
                next: 'p-lightbox-controller__button--next',
                altText: 'u-altText'
            },
            region: {
                wrap: 'p-carousel-region',
                text: 'p-carousel-region__text'
            }
        },
        text: {
            previous: '前へ',
            next: '次へ'
        },
        prefix: {
            id: 'lightbox-carousel-'
        }
    }));
};
